import { useState, useEffect, useCallback, useMemo } from "react";
import AdminLayout from "./AdminLayout";
import { api } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  Search, Filter, Eye, Loader2, Check, X,
  LayoutGrid, List, MapPin, Calendar, Truck, Users,
} from "lucide-react";

const formatStatus = (s) =>
  (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const formatBookingId = (id) => `BK-${(id || "").slice(0, 6).toUpperCase()}`;

const BOOKING_STAGES = [
  { key: "pending", label: "Pending", color: "border-yellow-400 bg-yellow-50" },
  { key: "deposit_paid", label: "Deposit Paid", color: "border-blue-400 bg-blue-50" },
  { key: "confirmed", label: "Confirmed", color: "border-green-400 bg-green-50" },
  { key: "completed", label: "Completed", color: "border-slate-400 bg-slate-50" },
];

const CREW_OPTIONS = ["Team Alpha", "Team Bravo", "Team Charlie", "Team Delta"];
const VAN_OPTIONS = ["Van 1 - Small", "Van 2 - Large", "Van 3 - Luton", "Van 4 - 7.5t"];

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("table");
  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [depositFilter, setDepositFilter] = useState("");
  const [crewFilter, setCrewFilter] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Modal
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [updating, setUpdating] = useState(false);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (depositFilter === "paid") params.append("deposit_paid", "true");
      if (depositFilter === "unpaid") params.append("deposit_paid", "false");
      if (crewFilter) params.append("assigned_crew", crewFilter);
      const response = await api.get(`/admin/bookings?${params.toString()}`);
      setBookings(response.data.bookings);
    } catch {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, depositFilter, crewFilter]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleUpdateStatus = async (bookingId, newStatus) => {
    setUpdating(true);
    try {
      await api.put(`/admin/bookings/${bookingId}?status=${newStatus}`);
      toast.success("Booking updated");
      fetchBookings();
      setSelectedBooking(null);
    } catch {
      toast.error("Failed to update booking");
    } finally {
      setUpdating(false);
    }
  };

  const handleAssign = async (bookingId, field, value) => {
    try {
      const payload = {};
      payload[field] = value;
      await api.put(`/admin/bookings/${bookingId}/assign`, payload);
      toast.success(`${field === "assigned_crew" ? "Crew" : "Van"} assigned`);
      fetchBookings();
    } catch {
      toast.error("Failed to assign");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-700",
      deposit_paid: "bg-blue-100 text-blue-700",
      confirmed: "bg-green-100 text-green-700",
      completed: "bg-slate-100 text-slate-700",
      cancelled: "bg-red-100 text-red-700",
    };
    return colors[status] || "bg-slate-100 text-slate-700";
  };

  const statuses = [
    { value: "pending", label: "Pending" },
    { value: "deposit_paid", label: "Deposit Paid" },
    { value: "confirmed", label: "Confirmed" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const filteredBookings = useMemo(
    () =>
      bookings.filter((b) => {
        if (!searchTerm) return true;
        const s = searchTerm.toLowerCase();
        return (
          b.booking_id?.toLowerCase().includes(s) ||
          b.customer?.name?.toLowerCase().includes(s) ||
          b.customer?.email?.toLowerCase().includes(s)
        );
      }),
    [bookings, searchTerm]
  );

  const pipelineData = useMemo(() => {
    const grouped = {};
    BOOKING_STAGES.forEach((s) => (grouped[s.key] = []));
    filteredBookings.forEach((b) => {
      if (grouped[b.status]) grouped[b.status].push(b);
    });
    return grouped;
  }, [filteredBookings]);

  const clearFilters = () => {
    setStatusFilter("");
    setSearchTerm("");
    setDepositFilter("");
    setCrewFilter("");
  };

  const hasActiveFilters = statusFilter || depositFilter || crewFilter;

  return (
    <AdminLayout title="Bookings">
      {/* Toolbar */}
      <div className="bg-white border border-slate-200 p-4 mb-4" data-testid="bookings-toolbar">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by booking ID or customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="bookings-search"
            />
          </div>
          <div className="flex gap-2 items-center">
            <div className="sm:w-40">
              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="bookings-status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {statuses.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant={showAdvanced ? "default" : "outline"}
              size="sm"
              onClick={() => setShowAdvanced(!showAdvanced)}
              data-testid="toggle-booking-filters"
            >
              <Filter className="w-4 h-4 mr-1" /> Filters
            </Button>
            <div className="flex border border-slate-200 rounded overflow-hidden">
              <button
                className={`px-3 py-2 text-sm ${viewMode === "table" ? "bg-[#0F172A] text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                onClick={() => setViewMode("table")}
                data-testid="bookings-view-table"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                className={`px-3 py-2 text-sm ${viewMode === "pipeline" ? "bg-[#0F172A] text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                onClick={() => setViewMode("pipeline")}
                data-testid="bookings-view-pipeline"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Advanced Filters */}
        {showAdvanced && (
          <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="booking-advanced-filters">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Deposit</label>
              <Select value={depositFilter || "all"} onValueChange={(v) => setDepositFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-deposit">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Assigned Crew</label>
              <Select value={crewFilter || "all"} onValueChange={(v) => setCrewFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-crew">
                  <SelectValue placeholder="Any Crew" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Crew</SelectItem>
                  {CREW_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="self-end text-slate-500">
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF5A1F]" />
        </div>
      ) : (
        viewMode === "pipeline"
          ? <BookingPipelineView
              stages={BOOKING_STAGES}
              data={pipelineData}
              getStatusColor={getStatusColor}
              onView={setSelectedBooking}
            />
          : <BookingTableView
              bookings={filteredBookings}
              getStatusColor={getStatusColor}
              onView={setSelectedBooking}
              onAssign={handleAssign}
            />
      )}

      {/* Booking Detail Modal */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Booking: {formatBookingId(selectedBooking?.booking_id)}</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <BookingModal
              booking={selectedBooking}
              statuses={statuses}
              getStatusColor={getStatusColor}
              onUpdateStatus={handleUpdateStatus}
              onAssign={handleAssign}
              updating={updating}
            />
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

/* --- Booking Pipeline View --- */
const BookingPipelineView = ({ stages, data, getStatusColor, onView }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="booking-pipeline-view">
    {stages.map((stage) => (
      <div key={stage.key} className={`border-t-4 ${stage.color} bg-white border border-slate-200 min-h-[200px]`}>
        <div className="p-3 border-b border-slate-100 flex items-center justify-between">
          <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">{stage.label}</span>
          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-bold">
            {data[stage.key]?.length || 0}
          </span>
        </div>
        <div className="p-2 space-y-2 max-h-[500px] overflow-y-auto">
          {(data[stage.key] || []).map((b) => (
            <div
              key={b.booking_id}
              className="bg-white border border-slate-200 p-3 hover:shadow-sm cursor-pointer transition-shadow"
              onClick={() => onView(b)}
              data-testid={`booking-card-${b.booking_id}`}
            >
              <div className="text-sm font-medium text-[#0F172A] truncate">{b.customer?.name || "N/A"}</div>
              {b.quote && (
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {b.quote.from_postcode} → {b.quote.to_postcode}
                </div>
              )}
              <div className="text-sm font-bold text-[#FF5A1F] mt-1">£{b.deposit_amount?.toFixed(2)}</div>
              <div className="flex gap-2 mt-2 text-[10px]">
                {b.assigned_crew && (
                  <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                    <Users className="w-2.5 h-2.5" /> {b.assigned_crew}
                  </span>
                )}
                {b.assigned_van && (
                  <span className="bg-slate-50 text-slate-600 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                    <Truck className="w-2.5 h-2.5" /> {b.assigned_van}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

/* --- Booking Table View --- */
const BookingTableView = ({ bookings, getStatusColor, onView, onAssign }) => (
  <div className="bg-white border border-slate-200 overflow-hidden" data-testid="booking-table-view">
    {bookings.length === 0 ? (
      <div className="p-8 text-center text-slate-500">No bookings found</div>
    ) : (
      <>
        <div className="sm:hidden px-3 py-1.5 bg-slate-50 text-xs text-slate-400 border-b border-slate-200">
          Scroll right to see all columns →
        </div>
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Booking</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Customer</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Route</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Deposit</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Crew</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Van</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {bookings.map((booking) => (
              <tr key={booking.booking_id} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="font-mono text-sm font-medium">{formatBookingId(booking.booking_id)}</div>
                  {booking.quote?.move_date && (
                    <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                      <Calendar className="w-3 h-3" /> {booking.quote.move_date}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-[#0F172A] text-sm">{booking.customer?.name || "N/A"}</div>
                  <div className="text-xs text-slate-400">{booking.customer?.email}</div>
                </td>
                <td className="px-4 py-3 text-sm">
                  {booking.quote ? `${booking.quote.from_postcode} → ${booking.quote.to_postcode}` : "N/A"}
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-[#FF5A1F] text-sm">£{booking.deposit_amount?.toFixed(2)}</div>
                  {booking.deposit_paid ? (
                    <span className="inline-flex items-center gap-0.5 text-green-600 text-xs"><Check className="w-3 h-3" /> Paid</span>
                  ) : (
                    <span className="inline-flex items-center gap-0.5 text-yellow-600 text-xs"><X className="w-3 h-3" /> Pending</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Select
                    value={booking.assigned_crew || "none"}
                    onValueChange={(v) => onAssign(booking.booking_id, "assigned_crew", v === "none" ? "" : v)}
                  >
                    <SelectTrigger className="h-8 text-xs w-[130px]" data-testid={`crew-select-${booking.booking_id}`}>
                      <SelectValue placeholder="Assign..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {CREW_OPTIONS.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <Select
                    value={booking.assigned_van || "none"}
                    onValueChange={(v) => onAssign(booking.booking_id, "assigned_van", v === "none" ? "" : v)}
                  >
                    <SelectTrigger className="h-8 text-xs w-[130px]" data-testid={`van-select-${booking.booking_id}`}>
                      <SelectValue placeholder="Assign..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {VAN_OPTIONS.map((v) => (
                        <SelectItem key={v} value={v}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(booking.status)}`}>
                    {formatStatus(booking.status)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => onView(booking)} data-testid={`view-booking-${booking.booking_id}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>
    )}
  </div>
);

/* --- Booking Modal --- */
const BookingModal = ({ booking, statuses, getStatusColor, onUpdateStatus, onAssign, updating }) => (
  <div className="space-y-5">
    {/* Customer */}
    <div className="bg-slate-50 p-4 rounded">
      <h3 className="font-semibold text-[#0F172A] mb-2 text-sm">Customer</h3>
      <div className="text-sm space-y-1">
        <div><strong>Name:</strong> {booking.customer?.name}</div>
        <div><strong>Email:</strong> {booking.customer?.email}</div>
        <div><strong>Phone:</strong> {booking.customer?.phone}</div>
      </div>
    </div>

    {/* Move Details */}
    {booking.quote && (
      <div className="grid grid-cols-2 gap-3">
        <div><div className="text-xs text-slate-500 uppercase mb-1">From</div><div className="font-medium text-sm">{booking.quote.from_postcode}</div></div>
        <div><div className="text-xs text-slate-500 uppercase mb-1">To</div><div className="font-medium text-sm">{booking.quote.to_postcode}</div></div>
        <div><div className="text-xs text-slate-500 uppercase mb-1">Move Date</div><div className="font-medium text-sm">{booking.quote.move_date}</div></div>
        <div><div className="text-xs text-slate-500 uppercase mb-1">Property</div><div className="font-medium text-sm">{booking.quote.bedrooms} bed {booking.quote.property_type}</div></div>
      </div>
    )}

    {/* Payment */}
    <div className="bg-[#0F172A] p-4 rounded text-white">
      <div className="flex justify-between items-center">
        <span className="text-sm">Deposit Amount</span>
        <span className="text-xl font-bold">£{booking.deposit_amount?.toFixed(2)}</span>
      </div>
      <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-700">
        <span className="text-sm">Payment Status</span>
        <span className={booking.deposit_paid ? "text-green-400" : "text-yellow-400"}>
          {booking.deposit_paid ? "Paid" : "Pending"}
        </span>
      </div>
      <div className="flex justify-between items-center mt-2">
        <span className="text-sm">Method</span>
        <span className="capitalize text-sm">{booking.payment_method}</span>
      </div>
    </div>

    {/* Assignments */}
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="text-xs text-slate-500 uppercase mb-1 block">Assigned Crew</label>
        <Select
          value={booking.assigned_crew || "none"}
          onValueChange={(v) => onAssign(booking.booking_id, "assigned_crew", v === "none" ? "" : v)}
        >
          <SelectTrigger data-testid="modal-crew-select">
            <Users className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Select crew" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Unassigned</SelectItem>
            {CREW_OPTIONS.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs text-slate-500 uppercase mb-1 block">Assigned Van</label>
        <Select
          value={booking.assigned_van || "none"}
          onValueChange={(v) => onAssign(booking.booking_id, "assigned_van", v === "none" ? "" : v)}
        >
          <SelectTrigger data-testid="modal-van-select">
            <Truck className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Select van" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Unassigned</SelectItem>
            {VAN_OPTIONS.map((v) => (
              <SelectItem key={v} value={v}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>

    {/* Status Update */}
    <div>
      <label className="text-xs text-slate-500 uppercase mb-2 block">Update Status</label>
      <Select
        defaultValue={booking.status}
        onValueChange={(v) => onUpdateStatus(booking.booking_id, v)}
      >
        <SelectTrigger data-testid="update-booking-status">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {statuses.map((s) => (
            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  </div>
);

export default AdminBookings;
